import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useI18n, SUPPORTED_LANGUAGES, ROUTE_MAPPINGS } from '../context/I18nContext';
import useMetaTags from '../hooks/useMetaTags';

const BASE_URL = 'https://codicefiscale.expert';
const OG_IMAGE = 'https://codicefiscale.expert/og-image.png';
const SITE_NAME = 'CodiceFiscale.expert';

// Locale mappings for Open Graph
const OG_LOCALES = {
  it: 'it_IT',
  en: 'en_US',
  de: 'de_DE',
  fr: 'fr_FR',
  es: 'es_ES',
  pt: 'pt_PT',
  ro: 'ro_RO',
  pl: 'pl_PL'
};

// Page type mappings for Schema.org
const PAGE_TYPES = {
  home: 'WebApplication',
  reverse: 'WebApplication',
  verify: 'WebApplication',
  healthCard: 'WebApplication',
  municipalities: 'WebApplication',
  howItWorks: 'HowTo',
  faq: 'FAQPage',
  guide: 'Article',
  omocodia: 'Article',
  privacy: 'WebPage',
  blog: 'Blog'
};

// HowTo Schema content - all 8 languages
const HOWTO_CONTENT = {
  it: {
    name: "Come calcolare il Codice Fiscale italiano",
    steps: [
      { name: "Inserisci il cognome", text: "Inserisci il cognome completo. Il sistema estrarrà le prime 3 consonanti (o vocali se necessario)." },
      { name: "Inserisci il nome", text: "Inserisci il nome completo. Se ha 4+ consonanti, si usano la 1ª, 3ª e 4ª." },
      { name: "Inserisci data di nascita", text: "Seleziona giorno, mese e anno. Per le donne si aggiunge 40 al giorno." },
      { name: "Seleziona luogo di nascita", text: "Cerca e seleziona il comune italiano o stato estero di nascita." },
      { name: "Genera il codice", text: "Clicca 'Calcola' per generare istantaneamente il Codice Fiscale con carattere di controllo." }
    ]
  },
  en: {
    name: "How to calculate the Italian Tax Code",
    steps: [
      { name: "Enter surname", text: "Enter the complete surname. The system will extract the first 3 consonants (or vowels if needed)." },
      { name: "Enter first name", text: "Enter the complete first name. If it has 4+ consonants, the 1st, 3rd, and 4th are used." },
      { name: "Enter birth date", text: "Select day, month, and year. For women, 40 is added to the day." },
      { name: "Select birthplace", text: "Search and select the Italian municipality or foreign country of birth." },
      { name: "Generate the code", text: "Click 'Calculate' to instantly generate the Tax Code with check character." }
    ]
  },
  de: {
    name: "Wie man die italienische Steuernummer berechnet",
    steps: [
      { name: "Nachname eingeben", text: "Geben Sie den vollständigen Nachnamen ein. Das System extrahiert die ersten 3 Konsonanten (oder Vokale falls nötig)." },
      { name: "Vorname eingeben", text: "Geben Sie den vollständigen Vornamen ein. Bei 4+ Konsonanten werden der 1., 3. und 4. verwendet." },
      { name: "Geburtsdatum eingeben", text: "Wählen Sie Tag, Monat und Jahr. Für Frauen werden 40 zum Tag addiert." },
      { name: "Geburtsort wählen", text: "Suchen und wählen Sie die italienische Gemeinde oder das Ausland als Geburtsort." },
      { name: "Code generieren", text: "Klicken Sie auf 'Berechnen', um sofort die Steuernummer mit Prüfzeichen zu generieren." }
    ]
  },
  fr: {
    name: "Comment calculer le Code Fiscal italien",
    steps: [
      { name: "Entrez le nom", text: "Entrez le nom complet. Le système extraira les 3 premières consonnes (ou voyelles si nécessaire)." },
      { name: "Entrez le prénom", text: "Entrez le prénom complet. S'il a 4+ consonnes, la 1ère, 3ème et 4ème sont utilisées." },
      { name: "Entrez la date de naissance", text: "Sélectionnez jour, mois et année. Pour les femmes, 40 est ajouté au jour." },
      { name: "Sélectionnez le lieu de naissance", text: "Recherchez et sélectionnez la commune italienne ou le pays étranger de naissance." },
      { name: "Générez le code", text: "Cliquez sur 'Calculer' pour générer instantanément le Code Fiscal avec caractère de contrôle." }
    ]
  },
  es: {
    name: "Cómo calcular el Código Fiscal italiano",
    steps: [
      { name: "Ingrese el apellido", text: "Ingrese el apellido completo. El sistema extraerá las primeras 3 consonantes (o vocales si es necesario)." },
      { name: "Ingrese el nombre", text: "Ingrese el nombre completo. Si tiene 4+ consonantes, se usan la 1ª, 3ª y 4ª." },
      { name: "Ingrese fecha de nacimiento", text: "Seleccione día, mes y año. Para mujeres, se añade 40 al día." },
      { name: "Seleccione lugar de nacimiento", text: "Busque y seleccione el municipio italiano o país extranjero de nacimiento." },
      { name: "Genere el código", text: "Haga clic en 'Calcular' para generar instantáneamente el Código Fiscal con carácter de control." }
    ]
  },
  pt: {
    name: "Como calcular o Código Fiscal italiano",
    steps: [
      { name: "Digite o sobrenome", text: "Digite o sobrenome completo. O sistema extrairá as primeiras 3 consoantes (ou vogais se necessário)." },
      { name: "Digite o nome", text: "Digite o nome completo. Se tiver 4+ consoantes, a 1ª, 3ª e 4ª são usadas." },
      { name: "Digite data de nascimento", text: "Selecione dia, mês e ano. Para mulheres, 40 é adicionado ao dia." },
      { name: "Selecione local de nascimento", text: "Pesquise e selecione o município italiano ou país estrangeiro de nascimento." },
      { name: "Gere o código", text: "Clique em 'Calcular' para gerar instantaneamente o Código Fiscal com caractere de controle." }
    ]
  },
  ro: {
    name: "Cum se calculează Codul Fiscal italian",
    steps: [
      { name: "Introduceți numele de familie", text: "Introduceți numele de familie complet. Sistemul va extrage primele 3 consoane (sau vocale dacă este necesar)." },
      { name: "Introduceți prenumele", text: "Introduceți prenumele complet. Dacă are 4+ consoane, se folosesc a 1-a, a 3-a și a 4-a." },
      { name: "Introduceți data nașterii", text: "Selectați ziua, luna și anul. Pentru femei, se adaugă 40 la zi." },
      { name: "Selectați locul nașterii", text: "Căutați și selectați comuna italiană sau țara străină de naștere." },
      { name: "Generați codul", text: "Faceți clic pe 'Calculează' pentru a genera instant Codul Fiscal cu caracterul de control." }
    ]
  },
  pl: {
    name: "Jak obliczyć włoski Kod Podatkowy",
    steps: [
      { name: "Wprowadź nazwisko", text: "Wprowadź pełne nazwisko. System wyodrębni pierwsze 3 spółgłoski (lub samogłoski jeśli to konieczne)." },
      { name: "Wprowadź imię", text: "Wprowadź pełne imię. Jeśli ma 4+ spółgłoski, używa się 1., 3. i 4." },
      { name: "Wprowadź datę urodzenia", text: "Wybierz dzień, miesiąc i rok. Dla kobiet do dnia dodaje się 40." },
      { name: "Wybierz miejsce urodzenia", text: "Wyszukaj i wybierz włoską gminę lub obce państwo urodzenia." },
      { name: "Wygeneruj kod", text: "Kliknij 'Oblicz', aby natychmiast wygenerować Kod Podatkowy ze znakiem kontrolnym." }
    ]
  }
};

// Breadcrumb labels per language
const BREADCRUMB_LABELS = {
  it: { home: 'Home', calculate: 'Calcola', reverse: 'Inverso', verify: 'Verifica', healthCard: 'Tessera Sanitaria', municipalities: 'Comuni', howItWorks: 'Come Funziona', faq: 'FAQ', guide: 'Guida', omocodia: 'Omocodia', blog: 'Blog' },
  en: { home: 'Home', calculate: 'Calculate', reverse: 'Reverse', verify: 'Verify', healthCard: 'Health Card', municipalities: 'Municipalities', howItWorks: 'How It Works', faq: 'FAQ', guide: 'Guide', omocodia: 'Omocodia', blog: 'Blog' },
  de: { home: 'Startseite', calculate: 'Berechnen', reverse: 'Umkehren', verify: 'Überprüfen', healthCard: 'Gesundheitskarte', municipalities: 'Gemeinden', howItWorks: 'Wie es funktioniert', faq: 'FAQ', guide: 'Anleitung', omocodia: 'Omocodia', blog: 'Blog' },
  fr: { home: 'Accueil', calculate: 'Calculer', reverse: 'Inverse', verify: 'Vérifier', healthCard: 'Carte Santé', municipalities: 'Communes', howItWorks: 'Comment ça marche', faq: 'FAQ', guide: 'Guide', omocodia: 'Omocodia', blog: 'Blog' },
  es: { home: 'Inicio', calculate: 'Calcular', reverse: 'Inverso', verify: 'Verificar', healthCard: 'Tarjeta Sanitaria', municipalities: 'Municipios', howItWorks: 'Cómo funciona', faq: 'FAQ', guide: 'Guía', omocodia: 'Omocodia', blog: 'Blog' },
  pt: { home: 'Início', calculate: 'Calcular', reverse: 'Inverso', verify: 'Verificar', healthCard: 'Cartão Saúde', municipalities: 'Municípios', howItWorks: 'Como funciona', faq: 'FAQ', guide: 'Guia', omocodia: 'Omocodia', blog: 'Blog' },
  ro: { home: 'Acasă', calculate: 'Calculează', reverse: 'Invers', verify: 'Verifică', healthCard: 'Card Sănătate', municipalities: 'Municipalități', howItWorks: 'Cum funcționează', faq: 'FAQ', guide: 'Ghid', omocodia: 'Omocodia', blog: 'Blog' },
  pl: { home: 'Strona główna', calculate: 'Oblicz', reverse: 'Odwróć', verify: 'Zweryfikuj', healthCard: 'Karta Zdrowia', municipalities: 'Gminy', howItWorks: 'Jak to działa', faq: 'FAQ', guide: 'Przewodnik', omocodia: 'Omocodia', blog: 'Blog' }
};

const SEOHead = ({ routeKey = 'home', customTitle, customDescription, articleData, faqData }) => {
  const { t, locale } = useI18n();

  // Get title and description from translations or custom props
  const title = customTitle || t(`meta.${routeKey}.title`);
  const description = customDescription || t(`meta.${routeKey}.description`);

  // Build canonical URL
  const buildUrl = (lang, route) => {
    if (lang === 'it') {
      return route ? `${BASE_URL}/${route}` : BASE_URL;
    }
    return route ? `${BASE_URL}/${lang}/${route}` : `${BASE_URL}/${lang}`;
  };

  const currentRoute = ROUTE_MAPPINGS[locale]?.[routeKey] || '';
  const canonicalUrl = buildUrl(locale, currentRoute);

  // Generate hreflang links for all languages
  const hreflangLinks = SUPPORTED_LANGUAGES.map(lang => {
    const route = ROUTE_MAPPINGS[lang.code]?.[routeKey] || '';
    const url = buildUrl(lang.code, route);
    return { lang: lang.code, url };
  });

  // Add x-default (Italian)
  const italianRoute = ROUTE_MAPPINGS.it[routeKey] || '';
  const xDefaultUrl = buildUrl('it', italianRoute);

  // Build BreadcrumbList Schema
  const breadcrumbLabel = BREADCRUMB_LABELS[locale]?.[routeKey] || BREADCRUMB_LABELS.en[routeKey] || routeKey;
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": BREADCRUMB_LABELS[locale]?.home || "Home",
        "item": locale === 'it' ? BASE_URL : `${BASE_URL}/${locale}`
      }
    ]
  };

  if (routeKey !== 'home') {
    breadcrumbSchema.itemListElement.push({
      "@type": "ListItem",
      "position": 2,
      "name": breadcrumbLabel,
      "item": canonicalUrl
    });
  }

  // Build WebApplication Schema for tool pages
  const webAppSchema = PAGE_TYPES[routeKey] === 'WebApplication' ? {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": title,
    "url": canonicalUrl,
    "description": description,
    "applicationCategory": "UtilityApplication",
    "operatingSystem": "Web Browser",
    "browserRequirements": "Requires JavaScript",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "provider": {
      "@type": "Organization",
      "name": SITE_NAME,
      "url": BASE_URL
    },
    "inLanguage": locale
  } : null;

  // Build HowTo Schema for howItWorks page (8 languages)
  const howToContent = HOWTO_CONTENT[locale] || HOWTO_CONTENT.en;
  const howToSchema = routeKey === 'howItWorks' ? {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": howToContent.name,
    "description": description,
    "totalTime": "PT2M",
    "inLanguage": locale,
    "tool": {
      "@type": "HowToTool",
      "name": "CodiceFiscale.expert Calculator"
    },
    "step": howToContent.steps.map((step, index) => ({
      "@type": "HowToStep",
      "position": index + 1,
      "name": step.name,
      "text": step.text
    }))
  } : null;

  // Article Schema for guide/omocodia pages
  const articleSchema = (routeKey === 'guide' || routeKey === 'omocodia' || articleData) ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": description,
    "url": canonicalUrl,
    "datePublished": articleData?.datePublished || "2024-01-01",
    "dateModified": articleData?.dateModified || "2026-01-23",
    "author": {
      "@type": "Organization",
      "name": SITE_NAME,
      "url": BASE_URL
    },
    "publisher": {
      "@type": "Organization",
      "name": SITE_NAME,
      "url": BASE_URL,
      "logo": {
        "@type": "ImageObject",
        "url": `${BASE_URL}/og-image.png`
      }
    },
    "inLanguage": locale,
    "mainEntityOfPage": canonicalUrl
  } : null;

  // Use custom hook to force meta tag updates
  useMetaTags({
    title: title,
    description: description,
    ogTitle: title,
    ogDescription: description,
    canonical: canonicalUrl,
    ogType: routeKey === 'blog' ? 'blog' : (routeKey === 'guide' || routeKey === 'omocodia') ? 'article' : 'website'
  });

  return (
    <Helmet prioritizeSeoTags defer={false}>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="author" content={SITE_NAME} />
      <meta name="publisher" content={SITE_NAME} />
      <meta name="copyright" content={`© 2026 ${SITE_NAME}`} />
      <link rel="canonical" href={canonicalUrl} />

      {/* hreflang for multilingual SEO */}
      {hreflangLinks.map(({ lang, url }) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={url} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={xDefaultUrl} />

      {/* Open Graph - Complete */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={routeKey === 'blog' ? 'blog' : (routeKey === 'guide' || routeKey === 'omocodia') ? 'article' : 'website'} />
      <meta property="og:locale" content={OG_LOCALES[locale] || 'it_IT'} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:image" content={OG_IMAGE} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />
      {/* Alternate locales for Facebook */}
      {SUPPORTED_LANGUAGES.filter(l => l.code !== locale).map(l => (
        <meta key={l.code} property="og:locale:alternate" content={OG_LOCALES[l.code]} />
      ))}

      {/* Twitter Card - Complete */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@codicefiscale" />
      <meta name="twitter:creator" content="@codicefiscale" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={OG_IMAGE} />
      <meta name="twitter:image:alt" content={title} />

      {/* Additional SEO meta tags */}
      <meta name="format-detection" content="telephone=no" />
      <meta name="geo.region" content="IT" />
      <meta name="geo.placename" content="Italy" />
      <meta name="ICBM" content="41.9028, 12.4964" />

      {/* Schema.org - BreadcrumbList (always) */}
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>

      {/* Schema.org - WebApplication (for tool pages) */}
      {webAppSchema && (
        <script type="application/ld+json">
          {JSON.stringify(webAppSchema)}
        </script>
      )}

      {/* Schema.org - HowTo (for how it works page) */}
      {howToSchema && (
        <script type="application/ld+json">
          {JSON.stringify(howToSchema)}
        </script>
      )}

      {/* Schema.org - Article (for guide/omocodia pages) */}
      {articleSchema && (
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      )}

      {/* Schema.org - FAQPage (passed from component) */}
      {faqData && (
        <script type="application/ld+json">
          {JSON.stringify(faqData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;
